'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, UserPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface RegisterFields {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  experience: string;
  terms: boolean;
}

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

export default function RegisterForm({ onSwitchToLogin }: RegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFields>();

  const password = watch('password');

  // Backend integration point: POST /api/auth/register with user details
  const onSubmit = async (_data: RegisterFields) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1600));
    setIsLoading(false);
    toast.success('Account created! Please sign in with your credentials.');
    onSwitchToLogin();
  };

  const inputClass = (hasError: boolean) =>
    `w-full px-3 py-2.5 rounded-lg text-sm transition-all duration-150 outline-none`;

  const inputStyle = (hasError: boolean) => ({
    background: 'var(--input)',
    border: `1px solid ${hasError ? 'var(--negative)' : 'var(--border)'}`,
    color: 'var(--foreground)',
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold mb-1">Create your account</h2>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          Start with 5 free AI trade ideas daily — no credit card required.
        </p>
      </div>

      {/* Full Name */}
      <div className="space-y-1.5">
        <label htmlFor="reg-name" className="block text-sm font-medium">
          Full Name
        </label>
        <input
          id="reg-name"
          type="text"
          autoComplete="name"
          placeholder="Arjun Sharma"
          className={inputClass(!!errors.fullName)}
          style={inputStyle(!!errors.fullName)}
          {...register('fullName', {
            required: 'Full name is required',
            minLength: { value: 2, message: 'Name must be at least 2 characters' },
          })}
        />
        {errors.fullName && (
          <p className="text-xs text-negative">{errors.fullName.message}</p>
        )}
      </div>

      {/* Email */}
      <div className="space-y-1.5">
        <label htmlFor="reg-email" className="block text-sm font-medium">
          Email Address
        </label>
        <input
          id="reg-email"
          type="email"
          autoComplete="email"
          placeholder="arjun@email.com"
          className={inputClass(!!errors.email)}
          style={inputStyle(!!errors.email)}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && (
          <p className="text-xs text-negative">{errors.email.message}</p>
        )}
      </div>

      {/* Phone */}
      <div className="space-y-1.5">
        <label htmlFor="reg-phone" className="block text-sm font-medium">
          Mobile Number
          <span className="ml-1 text-xs font-normal" style={{ color: 'var(--muted-foreground)' }}>
            (for SMS alerts)
          </span>
        </label>
        <input
          id="reg-phone"
          type="tel"
          autoComplete="tel"
          placeholder="+91 98765 43210"
          className={inputClass(!!errors.phone)}
          style={inputStyle(!!errors.phone)}
          {...register('phone', {
            required: 'Mobile number is required',
            pattern: { value: /^[+\d\s]{10,14}$/, message: 'Enter a valid mobile number' },
          })}
        />
        {errors.phone && (
          <p className="text-xs text-negative">{errors.phone.message}</p>
        )}
      </div>

      {/* Experience Level */}
      <div className="space-y-1.5">
        <label htmlFor="reg-exp" className="block text-sm font-medium">
          Trading Experience
          <span className="ml-1 text-xs font-normal" style={{ color: 'var(--muted-foreground)' }}>
            (helps us personalize your experience)
          </span>
        </label>
        <select
          id="reg-exp"
          className={inputClass(!!errors.experience)}
          style={{ ...inputStyle(!!errors.experience), appearance: 'none' }}
          {...register('experience', { required: 'Please select your experience level' })}
        >
          <option value="">Select experience level</option>
          <option value="beginner">Beginner — Less than 1 year</option>
          <option value="intermediate">Intermediate — 1–3 years</option>
          <option value="advanced">Advanced — 3+ years</option>
          <option value="professional">Professional / Full-time Trader</option>
        </select>
        {errors.experience && (
          <p className="text-xs text-negative">{errors.experience.message}</p>
        )}
      </div>

      {/* Password */}
      <div className="space-y-1.5">
        <label htmlFor="reg-password" className="block text-sm font-medium">
          Password
        </label>
        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          Minimum 8 characters with at least one number and one special character.
        </p>
        <div className="relative">
          <input
            id="reg-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Create a strong password"
            className={inputClass(!!errors.password)}
            style={{ ...inputStyle(!!errors.password), paddingRight: '40px' }}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Password must be at least 8 characters' },
              pattern: {
                value: /^(?=.*[0-9])(?=.*[!@#$%^&*])/,
                message: 'Must include at least one number and one special character',
              },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors duration-150"
            style={{ color: 'var(--muted-foreground)' }}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && (
          <p className="text-xs text-negative">{errors.password.message}</p>
        )}
      </div>

      {/* Confirm Password */}
      <div className="space-y-1.5">
        <label htmlFor="reg-confirm" className="block text-sm font-medium">
          Confirm Password
        </label>
        <div className="relative">
          <input
            id="reg-confirm"
            type={showConfirm ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Re-enter your password"
            className={inputClass(!!errors.confirmPassword)}
            style={{ ...inputStyle(!!errors.confirmPassword), paddingRight: '40px' }}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (val) => val === password || 'Passwords do not match',
            })}
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors duration-150"
            style={{ color: 'var(--muted-foreground)' }}
            aria-label={showConfirm ? 'Hide password' : 'Show password'}
          >
            {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.confirmPassword && (
          <p className="text-xs text-negative">{errors.confirmPassword.message}</p>
        )}
      </div>

      {/* Terms */}
      <div className="space-y-1.5">
        <label className="flex items-start gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            className="mt-0.5 w-4 h-4 rounded accent-primary flex-shrink-0"
            {...register('terms', {
              required: 'You must accept the terms to continue',
            })}
          />
          <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            I agree to the{' '}
            <span className="underline cursor-pointer" style={{ color: 'var(--accent)' }}>
              Terms of Service
            </span>{' '}
            and{' '}
            <span className="underline cursor-pointer" style={{ color: 'var(--accent)' }}>
              Privacy Policy
            </span>
            . I understand that trading involves market risk.
          </span>
        </label>
        {errors.terms && (
          <p className="text-xs text-negative">{errors.terms.message}</p>
        )}
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-150 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
        style={{ background: 'var(--primary)', color: 'var(--primary-foreground)', minHeight: '42px' }}
      >
        {isLoading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <>
            <UserPlus size={16} />
            Create Free Account
          </>
        )}
      </button>

      <p className="text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
        Already have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="font-medium transition-colors duration-150 hover:text-primary"
          style={{ color: 'var(--accent)' }}
        >
          Sign in here
        </button>
      </p>
    </form>
  );
}